import { useEffect, useRef, useState, type ReactNode } from "react";

import { classes } from "./classes.js";
import { RAIL_FOOT_AVATAR } from "./rail.js";

/**
 * `D-1019-1`, decision 3: **the avatar at the rail's foot is a menu.** « Profil » leaves the list of entries wherever an
 * avatar exists, and the avatar opens the profile and carries « Se déconnecter » (an app with no profile screen yet offers
 * only the way out).
 *
 * The panel is `fixed` beside the rail, never inside it: the rail is `overflow-hidden`, and a panel positioned inside it
 * is cut at its edge (the dealer's bell had the same fault, `rail-fit` / `rail.spec`). It closes on Escape, on a press
 * anywhere else, and when an item is chosen. This package holds no French: the words come from the app's catalogue.
 */
export interface RailMenuItem {
  readonly label: string;
  readonly onSelect: () => void;
  readonly testId?: string;
  /**
   * `AVATAR-D2`, A3: an 18 px icon drawn before the word (D2's `.ami`). Optional and additive: an item without one is the word
   * alone, exactly as before. Hidden from the reader, the word names the item.
   */
  readonly icon?: ReactNode;
}

export interface RailAvatarMenuProps {
  /** The initials drawn in the circle (`initials()` of the person's name). */
  readonly initials: string;
  /** The person's name, as the avatar's accessible name and tooltip. */
  readonly name: string;
  readonly items: readonly RailMenuItem[];
  /** The app's own colour for the circle (background and text classes). */
  readonly tone: string;
  readonly testId: string;
  /** Takes the whole foot row (a foot with an odd number of controls), centred. */
  readonly wide?: boolean;
  /**
   * `F-1c`: the line above the items, from the SESSION's answer: who is signed in, in what role, for which bank. A part the
   * session does not carry is absent, never guessed.
   */
  readonly identity?: {
    readonly name: string;
    readonly role: string;
    readonly organisation?: string;
    /**
     * `AVATAR-D2`, A2: the initials, drawn in a tile before the lines (D2's `.amh`), and the role and the organisation are
     * joined by « · ». Optional and additive: without it the line is as it was (no tile, « , »).
     */
    readonly tile?: string;
  };
  /**
   * `AVATAR-D2`, A6: opening the menu moves focus to its first item (D2's prototype does). Opt-in, so an app that does not
   * ask keeps today's behaviour: focus stays on the avatar.
   */
  readonly focusFirst?: boolean;
}

export function RailAvatarMenu({
  initials,
  name,
  items,
  tone,
  testId,
  wide = false,
  identity,
  focusFirst = false,
}: RailAvatarMenuProps): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    if (focusFirst) root.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    const away = (event: PointerEvent): void => {
      if (root.current !== null && event.target instanceof Node && !root.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const escape = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        setOpen(false);
        trigger.current?.focus();
      }
    };
    document.addEventListener("pointerdown", away);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", away);
      document.removeEventListener("keydown", escape);
    };
  }, [open, focusFirst]);

  return (
    <div ref={root} className={classes("flex justify-center", wide ? "col-span-2" : "")}>
      <button
        ref={trigger}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        title={name}
        aria-label={name}
        data-testid={testId}
        onClick={() => {
          setOpen((was) => !was);
        }}
        data-kit="rail-avatar"
        className={classes(RAIL_FOOT_AVATAR, tone)}
      >
        {initials}
      </button>
      {open ? (
        <div
          role="menu"
          aria-label={name}
          data-kit="avatar-menu"
          data-testid={`${testId}-menu`}
          className="fixed bottom-4 left-24 z-40 ml-2 w-48 rounded-xl border border-gray-200 bg-white p-1 text-left shadow-xl"
        >
          {identity === undefined ? null : identity.tile === undefined ? (
            <div data-kit="avatar-menu-identity" data-testid={`${testId}-identite`}>
              <p data-part="name">{identity.name}</p>
              <p data-part="role">
                {identity.role}
                {identity.organisation === undefined ? "" : `, ${identity.organisation}`}
              </p>
            </div>
          ) : (
            <div data-kit="avatar-menu-identity" data-tile="true" data-testid={`${testId}-identite`}>
              <span data-part="tile" aria-hidden="true">
                {identity.tile}
              </span>
              <div data-part="lines">
                <p data-part="name">{identity.name}</p>
                <p data-part="role">
                  {identity.role}
                  {identity.organisation === undefined ? "" : ` · ${identity.organisation}`}
                </p>
              </div>
            </div>
          )}
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              data-kit="avatar-menu-item"
              data-testid={item.testId}
              onClick={() => {
                setOpen(false);
                item.onSelect();
              }}
              className="block w-full rounded-lg px-3 py-2 text-left text-sm font-medium text-gray-700 hover:bg-indigo-50 hover:text-indigo-600"
            >
              {item.icon === undefined ? null : (
                <span data-part="icon" aria-hidden="true">
                  {item.icon}
                </span>
              )}
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
